import getEventBoxHeight from '@sdk/schedule/helpers/getEventBoxHeight';
import type { ShiftType } from '@sdk/shifts/types/ShiftType';
import type { EventType } from '@sdk/schedule/types/EventType';

import type { FormattedDateString } from '../types/FormattedDateString';
import type { RowType, MinutesSummaryByDay } from '../types/RowType';
import type { EventsByDate, TotalsPerDay } from '../types/GroupsArrayType';
import type { GroupsForDtstart } from '../types/GroupsType';
import type { ScheduleFilters, OptionsFilterType } from '../types';
import totalsModel from '../models/totalsModel';
import totalsWithLaborModel from '../models/totalsWithLaborModel';

import shallowCopyEvents from './helpers/shallowCopyEvents';
import getRowEvents from './rows/getRowEvents';
import getRegularRows from './rows/getRegularRows';
import isSpecialGroupVisible from './helpers/isSpecialGroupVisible';
import getRowHeight from './rows/getRowHeight';
import generateRowId from './helpers/generateRowId';
import getUniqueRowId from './helpers/getUniqueRowId';
import {
  HEADER_TEXT_HEIGHT,
  UNASSIGNED_SUMMARY_HEIGHT,
  SUMMARY_ROW_HEIGHT,
  UNASSIGNED,
  AVAILABLE,
  EVENT_TEXT_LINE_HEIGHT,
  BOX_VERTICAL_PADDING,
} from './constants';
import { UnassignedHeader, AvailableHeader } from './rows/Headers';

type GrouppedForRows = {
  available: {
    [key in FormattedDateString]: Array<ShiftType>;
  };
  unassigned: {
    [key in FormattedDateString]: Array<ShiftType>;
  };
  all: {
    [key in FormattedDateString]: Array<EventType>;
  };
  unassignedTotals: TotalsPerDay;
};

type Config = {
  scheduleFilters: ScheduleFilters;
  eventNumberOfLines: number;
  currentPeriodDays: Array<FormattedDateString>;
  optionsFilter: OptionsFilterType;
};

export default function makeGroupByDtstartRows(
  groups: GroupsForDtstart,
  { scheduleFilters, eventNumberOfLines, currentPeriodDays, optionsFilter }: Config
): Array<RowType> {
  currentPeriodDays.forEach((date) => {
    if (!groups[date]) {
      groups[date] = {
        available: [],
        events: [],
        totals: { ...totalsWithLaborModel },
        unassigned: [],
        unassignedTotals: { ...totalsModel },
      };
    }
  });

  const grouped = {
    unassigned: {},
    available: {},
    all: {},
    unassignedTotals: {},
    totals: {},
  };

  const eventHeight = getEventBoxHeight(eventNumberOfLines, {
    lineHeight: EVENT_TEXT_LINE_HEIGHT,
    verticalPadding: BOX_VERTICAL_PADDING,
  });
  const shouldDisplayUnassigned = isSpecialGroupVisible(UNASSIGNED, scheduleFilters.eventTypes);
  const shouldDisplayAvailable = isSpecialGroupVisible(AVAILABLE, scheduleFilters.eventTypes);
  const { showCountOfHours, showLaborCost, showLaborPercentage } = optionsFilter;
  const showSummary = showCountOfHours || showLaborCost || showLaborPercentage;

  Object.keys(groups).forEach((date: FormattedDateString) => {
    const { events, unassigned, available, unassignedTotals, totals } = groups[date];
    grouped.unassigned[date] = unassigned;
    grouped.available[date] = available;
    grouped.all[date] = events;
    grouped.unassignedTotals[date] = unassignedTotals;
    grouped.totals[date] = totals;
  });

  const rows = [];

  if (shouldDisplayUnassigned) {
    rows.push({
      id: 'UnassignedShiftsHeaderText',
      ...UnassignedHeader,
      rowData: {
        ...UnassignedHeader.rowData,
        hasNameColumn: false,
      },
    });

    rows.push(...getUnassignedRows(grouped, eventHeight));
  }

  if (shouldDisplayAvailable) {
    rows.push({
      id: 'AvailableShiftsHeaderText',
      ...AvailableHeader,
      rowData: {
        ...AvailableHeader.rowData,
        hasNameColumn: false,
        group: { isExchange: true, key: AVAILABLE, isForDtstart: true },
      },
    });

    rows.push(...getAvailableRows(grouped.available, eventHeight));
  }

  rows.push({
    id: 'ScheduledShiftsHeaderText',
    rowType: 'HeaderTextRow',
    rowData: {
      text: 'Scheduled',
      size: HEADER_TEXT_HEIGHT,
      hasNameColumn: false,
    },
  });

  rows.push(...getScheduledRows(grouped.all, eventHeight));

  if (showSummary) {
    rows.push({
      id: 'SummaryRow',
      rowType: 'SummaryRow',
      rowData: {
        totals: grouped.totals,
        size: SUMMARY_ROW_HEIGHT,
        hasNameColumn: false,
      },
    });
  } else if (rows.length) {
    rows[rows.length - 1].rowData.withBottomSeparator = true;
  }

  return rows;
}

function getUnassignedRows(grouped: GrouppedForRows, eventHeight: number): Array<RowType> {
  const events = shallowCopyEvents(grouped.unassigned);
  const unassignedRows = [];
  const firstRowEvents = getRowEvents(events);

  unassignedRows.push({
    id: generateRowId('unassignedFirstRow', firstRowEvents),
    rowType: 'SpecialGroupFirstRow',
    rowData: {
      events: firstRowEvents,
      size: getRowHeight(eventHeight, { isFirst: true }),
      eventHeight,
      hasNameColumn: false,
      type: UNASSIGNED,
      group: { isExchange: false, key: UNASSIGNED, isForDtstart: true },
    },
  });

  let rowEvents;
  do {
    rowEvents = getRowEvents(events);
    if (rowEvents && Object.keys(rowEvents).length) {
      unassignedRows.push({
        id: generateRowId('unassignedRegularRow', rowEvents),
        rowType: 'SpecialGroupRegularRow',
        rowData: {
          events: rowEvents,
          size: getRowHeight(eventHeight),
          eventHeight,
          hasNameColumn: false,
          type: UNASSIGNED,
          group: { isExchange: false, key: UNASSIGNED, isForDtstart: true },
        },
      });
    }
  } while (Object.keys(rowEvents).length);

  unassignedRows.push({
    id: 'UnassignedSummaryRow',
    rowType: 'UnassignedSummaryRow',
    rowData: {
      summary: getUnassignedSummary(grouped.unassignedTotals),
      size: UNASSIGNED_SUMMARY_HEIGHT,
      hasNameColumn: false,
    },
  });

  return unassignedRows;
}

function getAvailableRows(availableEvents: EventsByDate, eventHeight: number): Array<RowType> {
  const events = shallowCopyEvents(availableEvents);
  const availableRows = [];
  const firstRowEvents = getRowEvents(events);

  availableRows.push({
    id: generateRowId('availableFirstRow', firstRowEvents),
    rowType: 'SpecialGroupFirstRow',
    rowData: {
      events: firstRowEvents,
      size: getRowHeight(eventHeight, { isFirst: true }),
      eventHeight,
      hasNameColumn: false,
      type: AVAILABLE,
      group: { isExchange: true, key: AVAILABLE, isForDtstart: true },
    },
  });

  let rowEvents;
  do {
    rowEvents = getRowEvents(events);
    if (rowEvents && Object.keys(rowEvents).length) {
      availableRows.push({
        id: generateRowId('availableRegularRow', rowEvents),
        rowType: 'SpecialGroupRegularRow',
        rowData: {
          events: rowEvents,
          size: getRowHeight(eventHeight),
          eventHeight,
          hasNameColumn: false,
          type: AVAILABLE,
          group: { isExchange: false, key: AVAILABLE, isForDtstart: true },
        },
      });
    }
  } while (Object.keys(rowEvents).length);

  return availableRows;
}

function getScheduledRows(allEvents: EventsByDate, eventHeight: number): Array<RowType> {
  const events = shallowCopyEvents(allEvents);
  const rows = [];
  const rowIds = {};
  const firstRowEvents = getRowEvents(events);

  rows.push({
    id: getUniqueRowId(rowIds, generateRowId('FirstRow_events_', firstRowEvents)),
    rowType: 'RegularFirstRow',
    rowData: {
      events: firstRowEvents,
      size: getRowHeight(eventHeight, { isFirst: true }),
      eventHeight,
      hasNameColumn: false,
      group: { isExchange: false, key: 'events', isForDtstart: true },
    },
  });

  rows.push(
    ...getRegularRows({
      events,
      eventHeight,
      user: { id: 0 },
      group: { isExchange: false, key: 'events', isForDtstart: true, type: 'events' },
      hasNameColumn: false,
      rowIds,
    })
  );

  return rows;
}

function getUnassignedSummary(totalsPerDay: TotalsPerDay): MinutesSummaryByDay {
  return Object.keys(totalsPerDay).reduce(
    (result, date) => ({ ...result, [date]: totalsPerDay[date]?.paidMinutes || 0 }),
    {}
  );
}
